import { useMemo } from 'react'

import {
    useQuery,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query'
import { AxiosError } from 'axios'

import { getDownloads } from 'api/Cerebro/orgScoped/downloadsApi'
import { CerebroPaginatedResponse } from 'api/Cerebro/types'
import { useOrgContext } from 'context/OrgContext'
import { useCerebroApiRequest } from 'hooks'
import { Report } from 'types'

import { reportsDrawerQueryKey } from './reportsDownloadsKeys'

const DRAWER_PARAMS = {
    ordering: '-created_at',
    limit: 15,
} as const

interface UseReportsDrawerQueryProps {
    enabled: boolean
    options?: Omit<
        UseQueryOptions<CerebroPaginatedResponse<Report> | null, AxiosError>,
        'queryKey' | 'queryFn' | 'enabled'
    >
}

export function useReportsDrawerQuery({
    enabled,
    options,
}: UseReportsDrawerQueryProps): UseQueryResult<
    CerebroPaginatedResponse<Report> | null,
    AxiosError
> {
    const makeCerebroApiRequest = useCerebroApiRequest({ throwOnError: true })
    const { orgId, orgGroupId } = useOrgContext()

    const queryKey = useMemo(
        () => reportsDrawerQueryKey(orgId, orgGroupId),
        [orgGroupId, orgId]
    )

    return useQuery({
        queryKey,
        ...options,
        enabled: !!orgId && enabled,
        queryFn: async (): Promise<CerebroPaginatedResponse<Report> | null> =>
            makeCerebroApiRequest({
                request: getDownloads(DRAWER_PARAMS),
            }),
    })
}
